import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  MoreVertical,
  Trash2,
  Flame,
  Droplets,
  Moon,
  Brain,
  Zap,
  Footprints,
  Utensils,
  HeartPulse,
  Flower2,
  Star,
  CheckCircle2,
  Hash,
  Clock,
  Gauge,
  ListChecks,
} from "lucide-react";
import {
  useClientHabits,
  HabitClient,
  HabitCategory,
  HABIT_CATEGORIES,
  TRACKING_TYPES,
} from "@/hooks/use-habits";
import { AssignHabitDialog } from "./AssignHabitDialog";
import { toast } from "sonner";

const CATEGORY_ICONS: Record<HabitCategory, React.ReactNode> = {
  hydration: <Droplets className="h-4 w-4" />,
  sleep: <Moon className="h-4 w-4" />,
  stress: <Brain className="h-4 w-4" />,
  energy: <Zap className="h-4 w-4" />,
  movement: <Footprints className="h-4 w-4" />,
  nutrition: <Utensils className="h-4 w-4" />,
  recovery: <HeartPulse className="h-4 w-4" />,
  mindfulness: <Flower2 className="h-4 w-4" />,
  custom: <Star className="h-4 w-4" />,
};

const TRACKING_ICONS: Record<string, React.ReactNode> = {
  boolean: <CheckCircle2 className="h-3 w-3" />,
  count: <Hash className="h-3 w-3" />,
  duration: <Clock className="h-3 w-3" />,
  quality_score: <Gauge className="h-3 w-3" />,
};

interface HabitAssignmentRowProps {
  assignment: HabitClient;
  onUnassign: (assignment: HabitClient) => void;
}

function HabitAssignmentRow({ assignment, onUnassign }: HabitAssignmentRowProps) {
  const habit = assignment.habit;
  if (!habit) return null;

  const categoryMeta = HABIT_CATEGORIES[habit.category];
  const trackingMeta = TRACKING_TYPES[habit.tracking_type];

  return (
    <div className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-3">
        <div
          className={`h-9 w-9 rounded-lg bg-muted flex items-center justify-center ${categoryMeta.color}`}
        >
          {CATEGORY_ICONS[habit.category]}
        </div>
        <div>
          <div className="font-medium text-sm">{habit.name}</div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              {TRACKING_ICONS[habit.tracking_type]}
              {trackingMeta.label}
            </span>
            {assignment.target_value && habit.unit && (
              <>
                <span>•</span>
                <span>Target: {assignment.target_value} {habit.unit}</span>
              </>
            )}
            <span>•</span>
            <span className="capitalize">{assignment.frequency.replace("_", " ")}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {/* TODO: Add streak badge once we implement streak calculation on frontend */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => onUnassign(assignment)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

interface ClientHabitsCardProps {
  clientId: string;
}

export function ClientHabitsCard({ clientId }: ClientHabitsCardProps) {
  const { assignments, loading, unassignHabit, refetch } = useClientHabits(clientId);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [unassignTarget, setUnassignTarget] = useState<HabitClient | null>(null);
  const [isUnassigning, setIsUnassigning] = useState(false);

  const handleUnassign = async () => {
    if (!unassignTarget) return;

    setIsUnassigning(true);
    try {
      await unassignHabit(unassignTarget.id);
      toast.success("Habit removed from client");
    } catch (error) {
      toast.error("Failed to remove habit");
    } finally {
      setIsUnassigning(false);
      setUnassignTarget(null);
    }
  };

  const handleAssignSuccess = () => {
    setAssignDialogOpen(false);
    refetch();
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5" />
            Assigned Habits
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse text-muted-foreground">Loading habits...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ListChecks className="h-5 w-5" />
              Assigned Habits
            </CardTitle>
            <CardDescription>
              Lifestyle habits assigned to this client
            </CardDescription>
          </div>
          <Button onClick={() => setAssignDialogOpen(true)} size="sm" className="gap-2">
            <Plus className="h-4 w-4" />
            Assign Habit
          </Button>
        </CardHeader>
        <CardContent>
          {assignments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Flower2 className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold mb-2">No Habits Assigned</h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                Assign lifestyle habits to help this client track behaviors like hydration,
                sleep, stress management, and more.
              </p>
              <Button onClick={() => setAssignDialogOpen(true)} variant="outline" className="gap-2">
                <Plus className="h-4 w-4" />
                Assign First Habit
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {assignments.map((assignment) => (
                <HabitAssignmentRow
                  key={assignment.id}
                  assignment={assignment}
                  onUnassign={setUnassignTarget}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Habit Dialog */}
      <AssignHabitDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        clientId={clientId}
        existingHabitIds={assignments.map((a) => a.habit_id)}
        onSuccess={handleAssignSuccess}
      />

      {/* Unassign Confirmation */}
      <AlertDialog open={!!unassignTarget} onOpenChange={() => setUnassignTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Habit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{unassignTarget?.habit?.name}" from this client?
              Their completion history will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnassigning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnassign}
              disabled={isUnassigning}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isUnassigning ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
